import {useEffect, useRef, useState} from "react";

export const useCalculateFontSize = (text) => {
  const containerRef = useRef(null);
  const [fontSize, setFontSize] = useState(80);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !text) {
      setFontSize(80);
      return;
    }

    const calculateFontSize = () => {
      const {width, height} = el.getBoundingClientRect();

      // Create canvas to measure actual text width
      const canvas = document.createElement("canvas");
      const context = canvas.getContext("2d");

      if (!context) return;

      // Start with a generous base size (60% of smaller dimension)
      const minDimension = Math.min(width, height);
      let calculatedSize = minDimension * 0.6;

      // Apply max constraint
      const maxSize = 300;
      calculatedSize = Math.min(maxSize, calculatedSize);

      // Add padding to avoid tight fit
      const availableWidth = width * 0.85;
      const availableHeight = height * 0.85;

      // Iteratively reduce font size until text fits both width and height
      let iterations = 0;
      const maxIterations = 20;

      while (iterations < maxIterations) {
        context.font = `bold ${calculatedSize}px Roboto, Arial`;
        const metrics = context.measureText(text);
        const textWidth = metrics.width;
        const textHeight = calculatedSize * 1.2; // Approximate line height

        // Check if it fits
        if (textWidth <= availableWidth && textHeight <= availableHeight) {
          break;
        }

        // Calculate scale factors for width and height
        const widthScale = textWidth > availableWidth ? availableWidth / textWidth : 1;
        const heightScale =
          textHeight > availableHeight ? availableHeight / textHeight : 1;

        // Use the more restrictive scale factor
        const scale = Math.min(widthScale, heightScale);
        calculatedSize = calculatedSize * scale * 0.95; // 0.95 for safety margin

        iterations++;
      }

      // Apply minimum constraint
      const minSize = 20;
      calculatedSize = Math.max(minSize, calculatedSize);

      setFontSize(Math.floor(calculatedSize));
    };

    // Calculate initially
    calculateFontSize();

    // Recalculate on resize
    const resizeObserver = new ResizeObserver(calculateFontSize);
    resizeObserver.observe(el);

    return () => resizeObserver.disconnect();
  }, [text]);

  return {containerRef, fontSize};
};
